// apps/web/src/pages/spread/SpreadRenderPane.tsx
//
// Phase 11.2 Plan 02 (Wave 1) — the THIN form-state-to-props adapter.
//
// After the Plan 11.2-02 extraction, the render engine itself lives in
// apps/web/src/render/RecipeRenderEngine.tsx (form-state-free, fetch-free,
// reused by the Author preview + the two view routes). This module keeps ONLY
// the form coupling SpreadComposer depends on:
//   - useFormContext + useWatch({name:"batchSets"}) — the single form read.
//   - the active-batch-set / active-recipe resolution.
//   - the recipe useQuery (api() helper; envelope.body -> RecipeDefinitionV1).
// It dispatches into the co-located status panels (RenderEmpty / RenderLoading /
// RenderError) and, on success, renders <RecipeRenderEngine> inside a
// RecipeStateProvider scoped `spread:${spreadId}` (preserving the per-spread
// widget-state isolation that the old inner provider wrapper established).
//
// SpreadComposer is UNTOUCHED: the exported SpreadRenderPaneProps {spreadId} and
// the SpreadRenderPane signature are unchanged, so the composer wiring still
// mounts this adapter exactly as before. The SpreadRenderPane.test.tsx 7-test
// suite is the equivalence gate — it runs UNMODIFIED against this reduced
// adapter (the adapter+engine composite is behavior-identical to the old
// monolith), proving the extraction is behavior-preserving.
//
// HOOK-ORDER RULE: useFormContext + useWatch + useQuery all run BEFORE any
// conditional return. enabled={true|false} keeps the query effect from firing
// the actual fetch when no recipe is selected.
//
// MISSION CRITICAL:
//   - api() helper is the single network surface; carries credentials and
//     ApiError on non-2xx (auth gates surface via err.status).
//   - envelope.body is cast to RecipeDefinitionV1 and threaded into the engine;
//     Phase 6's per-widget configSchema.safeParse provides defense-in-depth
//     re-validation at render time (T-09-15-01 / T-11.2-02 mitigation).

import { useQuery } from "@tanstack/react-query";
import type { RecipeDefinitionV1 } from "@the-recipe-spread/shared";
import { useFormContext, useWatch } from "react-hook-form";
import { api } from "@/lib/api-client";
import {
  RecipeRenderEngine,
  RenderEmpty,
  RenderError,
  RenderLoading,
} from "@/render/RecipeRenderEngine";
import { RecipeStateProvider } from "@/widgets/recipe-state-provider";

export interface SpreadRenderPaneProps {
  spreadId: string;
}

// ---------------------------------------------------------------------------
// Top-level pane — picks the active batch set, fires the recipe-load query,
// and dispatches into empty / loading / error / success branches. The success
// branch mounts the extracted engine inside a per-spread state provider.
// ---------------------------------------------------------------------------

export function SpreadRenderPane(props: SpreadRenderPaneProps): React.ReactElement {
  const { control } = useFormContext();
  // Per-path useWatch — narrow subscription to the batchSets array. Identity
  // changes only when batchSets is reassigned (RHF immutability guarantee).
  const batchSets = useWatch({ control, name: "batchSets" }) as
    | ReadonlyArray<{ recipeId?: string }>
    | undefined;

  const normalized: ReadonlyArray<{ recipeId?: string }> = batchSets ?? [];
  // First batch-set index with a non-empty recipeId string — preserves the
  // Plan 09-11 selection-resolution semantic. The empty-state branch fires
  // when no such index exists.
  const activeBatchSetIndex = normalized.findIndex(
    (bs) => typeof bs.recipeId === "string" && bs.recipeId.length > 0,
  );
  const activeRecipeId =
    activeBatchSetIndex >= 0
      ? (normalized[activeBatchSetIndex]?.recipeId ?? "")
      : "";

  // useQuery is ALWAYS called (hook-order invariant); `enabled` gates the
  // actual fetch on a real recipe selection. queryKey scopes by recipe id so
  // multiple spreads can coexist without cache collision.
  const recipeQuery = useQuery<RecipeDefinitionV1>({
    queryKey: ["recipe", activeRecipeId],
    enabled: activeRecipeId.length > 0,
    queryFn: async () => {
      const envelope = await api<{ body: unknown }>(`/api/recipes/${activeRecipeId}`);
      // The engine's TemplateRenderer re-validates each widget's config via
      // configSchema.safeParse — defense-in-depth at render time. The cast
      // here is the boundary; downstream consumers narrow per-widget.
      return envelope.body as RecipeDefinitionV1;
    },
  });

  if (activeBatchSetIndex < 0) {
    return <RenderEmpty />;
  }

  if (recipeQuery.isLoading) {
    return <RenderLoading />;
  }

  if (recipeQuery.isError || recipeQuery.data === undefined) {
    return <RenderError error={recipeQuery.error} />;
  }

  // Success — mount the extracted engine inside a RecipeStateProvider scoped by
  // `spread:{spreadId}` so multiple spreads on the same page don't share widget
  // state (preserves the old inner-wrapper contract). The engine reads this
  // provider's context for widgetStates; it does NOT create its own.
  return (
    <div className="flex flex-col gap-4 p-4">
      <RecipeStateProvider recipeId={`spread:${props.spreadId}`}>
        <RecipeRenderEngine
          recipe={recipeQuery.data}
          batchSets={normalized}
          activeBatchSetIndex={activeBatchSetIndex}
        />
      </RecipeStateProvider>
    </div>
  );
}
SpreadRenderPane.displayName = "SpreadRenderPane";
